(() => {
  'use strict';
  const requests = new Map();
  const handlePattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const optionKey = (name) => ['modelo', 'modelagem'].includes(String(name).trim().toLowerCase()) ? 'modelagem' : String(name).normalize('NFC').trim().toLowerCase();
  const optionValue = (value) => String(value).normalize('NFC');
  function productURL(handle, ajax = false) {
    if (!handlePattern.test(handle)) throw new Error('INVALID_HANDLE');
    const root = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    if (root.origin !== window.location.origin || root.search || root.hash) throw new Error('INVALID_STOREFRONT_ROOT');
    const prefix = root.pathname.replace(/\/?$/, '/');
    const result = new URL(prefix + 'products/' + handle + (ajax ? '.js' : ''), window.location.origin);
    if (result.origin !== window.location.origin) throw new Error('INVALID_PRODUCT_ORIGIN');
    return result;
  }
  function variantID(variant) {
    if (typeof variant?.id === 'number' && !Number.isSafeInteger(variant.id)) return null;
    const id = String(variant?.id || '');
    return /^[1-9]\d{0,19}$/.test(id) ? id : null;
  }
  function nativeOptions(product) {
    return (product.options || []).map((option) => typeof option === 'string' ? option : option?.name);
  }
  function resolveVariant(product, wanted) {
    if (!wanted || typeof wanted !== 'object') return null;
    const names = nativeOptions(product);
    if (!names.length || names.length > 3 || names.some((name) => typeof name !== 'string')) return null;
    const wantedMap = Object.fromEntries(Object.entries(wanted).map(([name, value]) => [optionKey(name), optionValue(value)]));
    const keys = names.map(optionKey);
    if (new Set(keys).size !== names.length || Object.keys(wantedMap).length !== names.length || keys.some((key) => !(key in wantedMap))) return null;
    const matches = product.variants.filter((variant) => Array.isArray(variant.options) && variant.options.length === names.length
      && keys.every((key, index) => optionValue(variant.options[index]) === wantedMap[key]));
    return matches.length === 1 && variantID(matches[0]) ? matches[0] : null;
  }
  function loadProduct(handle) {
    const url = productURL(handle, true);
    if (requests.has(url.href)) return requests.get(url.href);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    const request = fetch(url.href, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal })
      .then((response) => {
        if (!response.ok || response.redirected) throw new Error(response.status === 404 ? 'NOT_PUBLISHED' : 'PRODUCT_UNAVAILABLE');
        if (!String(response.headers.get('Content-Type') || '').includes('application/json')) throw new Error('NOT_PRODUCT_JSON');
        return response.json();
      })
      .then((product) => {
        if (product?.handle !== handle || !Array.isArray(product.variants) || !product.variants.length || product.variants.length > 250) throw new Error('PRODUCT_IDENTITY_MISMATCH');
        return product;
      })
      .catch((error) => { requests.delete(url.href); throw error; })
      .finally(() => window.clearTimeout(timer));
    requests.set(url.href, request);
    return request;
  }
  function destination(product, wanted) {
    const variant = resolveVariant(product, wanted);
    if (!variant) return null;
    const url = productURL(product.handle);
    url.searchParams.set('variant', variantID(variant));
    return { url: url.href, available: variant.available === true };
  }
  function selectedNativeID(region) {
    const fromURL = new URL(window.location.href).searchParams.get('variant');
    if (fromURL) return variantID({ id: fromURL });
    const script = document.querySelector('main variant-picker script[type="application/json"]');
    if (script) { try { return variantID(JSON.parse(script.textContent)); } catch (_) { return null; } }
    return variantID({ id: region.dataset.orsoInitialVariant });
  }
  function currentNativeChoice(product, region) {
    const picker = document.querySelector('main variant-picker');
    let choices = [];
    if (picker) {
      if (typeof picker.getAllSelectedOptions === 'function') choices = picker.getAllSelectedOptions();
      else {
        for (const select of picker.querySelectorAll('select')) {
          const option = select.selectedOptions?.[0];
          if (option?.dataset?.optionName) choices.push({ name: option.dataset.optionName, value: option.value });
        }
        for (const input of picker.querySelectorAll('fieldset input:checked')) {
          if (input.dataset?.optionName) choices.push({ name: input.dataset.optionName, value: input.value });
        }
      }
      if (!choices.length) return null;
    } else {
      const selectedID = selectedNativeID(region);
      const variant = product.variants.find((item) => variantID(item) === selectedID);
      if (!variant) return null;
      choices = nativeOptions(product).map((name, i) => ({ name, value: variant.options[i] }));
    }
    const wanted = Object.fromEntries(choices.map((choice) => [choice.name, choice.value]));
    if (!resolveVariant(product, wanted)) return null;
    const fingerprint = JSON.stringify(Object.entries(wanted).map(([name, value]) => [optionKey(name), optionValue(value)]).sort((a, b) => a[0].localeCompare(b[0])));
    return { wanted, fingerprint };
  }
  function initializePeers(scope = document) {
    scope.querySelectorAll('[data-orso-native-peers]').forEach((region) => {
      if (region.dataset.orsoPeersReady) return;
      region.dataset.orsoPeersReady = 'true';
      const status = region.querySelector('[data-orso-peer-status]');
      region.querySelectorAll('[data-orso-native-peer]').forEach((link) => {
        link.addEventListener('click', async (event) => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.getAttribute('aria-current') === 'page') return;
          event.preventDefault();
          if (region.getAttribute('aria-busy') === 'true') return;
          const startingPath = new URL(window.location.href).pathname;
          region.setAttribute('aria-busy', 'true');
          status.textContent = 'Conferindo as opções da outra modelagem…';
          try {
            const current = await loadProduct(region.dataset.orsoCurrentHandle);
            const selected = currentNativeChoice(current, region);
            if (!selected) throw new Error('CURRENT_VARIANT_NOT_CONFIRMED');
            const peer = await loadProduct(link.dataset.orsoNativePeer);
            const target = destination(peer, selected.wanted);
            if (!target) throw new Error('NO_EXACT_VARIANT');
            const latest = currentNativeChoice(current, region);
            if (region.isConnected === false || new URL(window.location.href).pathname !== startingPath || latest?.fingerprint !== selected.fingerprint) throw new Error('SELECTION_CHANGED');
            window.location.assign(target.url);
          } catch (_) { status.textContent = 'Não foi possível manter essa combinação na outra modelagem. Volte ao catálogo para escolher as opções.'; }
          finally { region.removeAttribute('aria-busy'); }
        });
      });
    });
  }
  let nativeSubmissions = new WeakMap();
  let nativeCheckoutPending = false;
  function nativeForm(target) {
    const form = target?.closest?.('product-form-component form[action]');
    if (!form) return null;
    try {
      const action = new URL(form.getAttribute('action'), window.location.origin);
      if (action.origin !== window.location.origin || !action.pathname.endsWith('/cart/add')) return null;
    } catch (_) { return null; }
    return form;
  }
  function nativeStatus(message, form = null) {
    if (form) {
      let feedback = form.closest('product-form-component')?.querySelector('[ref="liveRegion"]') || form.querySelector('[data-orso-submit-feedback]');
      if (!feedback) {
        feedback = document.createElement('p');
        feedback.className = 'orso-pdp-add-status';
        feedback.setAttribute('data-orso-submit-feedback', '');
        feedback.setAttribute('role', 'status');
        feedback.setAttribute('aria-live', 'polite');
        (form.querySelector('.product-form-buttons') || form).append(feedback);
      }
      feedback.textContent = message;
      return;
    }
    const status = document.querySelector('[data-orso-native-submit-status]');
    if (status) status.textContent = message;
  }
  function cancelNative(event, message) {
    event.preventDefault();
    event.stopImmediatePropagation();
    nativeStatus(message, nativeForm(event.target));
  }
  function rejectRepeated(event) {
    cancelNative(event, 'Aguarde a confirmação. Se a página não avançar, confira o carrinho antes de tentar novamente.');
  }
  function nativeState(form) {
    let stock;
    try { stock = JSON.parse(document.querySelector('[data-orso-native-stock]')?.textContent || 'null'); } catch (_) { return { error: 'Não foi possível conferir esta opção. Confira as opções do produto antes de continuar.' }; }
    const component = form.closest('product-form-component');
    const id = String(form.querySelector('[name="id"]')?.value || '');
    if (!stock || String(component?.dataset?.productId || '') !== String(stock.productId) || !/^[1-9]\d{0,19}$/.test(id) || !Array.isArray(stock.variants) || stock.variants.length > 250 || !Array.isArray(stock.optionNames)) return { error: 'Não foi possível conferir esta opção. Confira as opções do produto antes de continuar.' };
    const matches = stock.variants.filter((variant) => variantID(variant) === id);
    if (matches.length !== 1) return { error: 'Selecione uma combinação existente antes de continuar.' };
    const variant = matches[0];
    if (variant.available !== true) return { error: 'Esta combinação está indisponível no momento. Escolha outra opção.' };
    const product = { options: stock.optionNames, variants: stock.variants };
    const choice = currentNativeChoice(product, { dataset: { orsoInitialVariant: id } });
    if (!choice || variantID(resolveVariant(product, choice.wanted)) !== id) return { error: 'Aguarde a atualização das opções escolhidas antes de continuar.' };
    const rawQuantity = String(form.querySelector('[name="quantity"]')?.value || component?.dataset?.quantityDefault || '1');
    if (!/^[1-9]\d{0,5}$/.test(rawQuantity)) return { error: 'Informe uma quantidade válida antes de continuar.' };
    const quantity = Number(rawQuantity), min = Number(variant.quantityMin || 1), increment = Number(variant.quantityIncrement || 1), max = variant.quantityMax == null ? null : Number(variant.quantityMax);
    if (!Number.isSafeInteger(quantity) || !Number.isSafeInteger(min) || min < 1 || !Number.isSafeInteger(increment) || increment < 1 || quantity < min || (quantity - min) % increment !== 0 || (max != null && (!Number.isSafeInteger(max) || quantity > max)) || (typeof form.checkValidity === 'function' && !form.checkValidity())) return { error: 'Confira a quantidade permitida para esta opção.' };
    return { id, quantity };
  }
  function canSubmitNative(form) {
    if (!form || nativeForm(form) !== form) return false;
    const state = nativeState(form);
    if (state.error) { nativeStatus(state.error, form); return false; }
    if (nativeSubmissions.has(form)) {
      nativeStatus('Aguarde a confirmação. Se a página não avançar, confira o carrinho antes de tentar novamente.', form);
      return false;
    }
    return true;
  }
  function nativeCartLineID(value) {
    const text = String(value || '');
    return variantID({ id: text.startsWith('gid://shopify/ProductVariant/') ? text.slice(29) : text });
  }
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add' || event.context !== 'product' || !event.promise?.then) return;
    const component = event.target?.closest?.('product-form-component');
    const form = component?.querySelector('form[action]');
    if (!form || nativeForm(form) !== form || !Array.isArray(event.lines) || !event.lines.length) return;
    const expected = new Map();
    for (const line of event.lines) {
      const id = nativeCartLineID(line.merchandiseId), quantity = Number(line.quantity);
      if (!id || !Number.isSafeInteger(quantity) || quantity < 1) return;
      const total = (expected.get(id) || 0) + quantity;
      if (!Number.isSafeInteger(total)) return;
      expected.set(id, total);
    }
    const previous = nativeSubmissions.get(form);
    if (previous?.promise) return;
    const lock = { promise: event.promise, productId: String(component.dataset.productId || ''), sourceId: String(component.id || ''), phase: 'pending' };
    nativeSubmissions.set(form, lock);
    nativeStatus('Adicionando ao carrinho. Aguarde a confirmação.', form);
    const uncertain = () => {
      if (nativeSubmissions.get(form) !== lock) return;
      lock.phase = 'uncertain';
      nativeStatus('Não foi possível confirmar a adição. Confira o carrinho antes de tentar novamente.', form);
    };
    Promise.resolve(event.promise).then((result) => {
      if (nativeSubmissions.get(form) !== lock) return;
      const detail = result?.detail;
      if (detail?.source !== 'product-form-component' || String(detail.productId || '') !== lock.productId || String(detail.sourceId || '') !== lock.sourceId || !result.cart || !Array.isArray(detail.items)) { uncertain(); return; }
      if (detail.didError === true) {
        uncertain();
        return;
      }
      if (detail.didError !== false || result.userErrors?.length) { uncertain(); return; }
      const quantities = new Map();
      for (const item of detail.items) {
        const id = nativeCartLineID(item.variant_id ?? item.id), quantity = Number(item.quantity);
        if (id && Number.isSafeInteger(quantity) && quantity > 0) quantities.set(id, (quantities.get(id) || 0) + quantity);
      }
      if ([...expected].some(([id, quantity]) => (quantities.get(id) || 0) < quantity)) { uncertain(); return; }
      nativeSubmissions.delete(form);
      nativeStatus('Camiseta adicionada ao carrinho. Confira o pedido no carrinho.', form);
    }).catch(uncertain);
  }, true);
  function cartCheckoutControl(target) {
    return target?.closest?.('button[name="checkout"],input[name="checkout"],.ymp_elem_mainBtn,a[href="/checkout"]') || null;
  }
  function navigateToCart(event) {
    if (nativeCheckoutPending) { rejectRepeated(event); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    let target;
    try {
      const root = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
      if (root.origin !== window.location.origin || root.search || root.hash) throw new Error('INVALID_ROOT');
      target = new URL(root.pathname.replace(/\/?$/, '/') + 'cart', window.location.origin);
    } catch (_) { nativeStatus('Não foi possível abrir o carrinho. Volte ao catálogo e tente pelo link Carrinho.'); return; }
    nativeCheckoutPending = true;
    nativeStatus('Abrindo o carrinho para conferir o pedido.');
    window.location.assign(target.href);
  }
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const checkout = cartCheckoutControl(event.target);
    if (checkout) {
      if (checkout.disabled) return;
      if (!/\/cart\/?$/.test(window.location.pathname)) { navigateToCart(event); return; }
      let checkoutReady = false;
      try {
        const checkoutURL = new URL(String(window.yampiCheckoutUrl || ''));
        checkoutReady = checkout.classList?.contains('ymp_elem_mainBtn') && checkoutURL.protocol === 'https:' && !checkoutURL.username && !checkoutURL.password;
      } catch (_) { checkoutReady = false; }
      if (!checkoutReady) { cancelNative(event, 'O checkout está sendo conferido. Aguarde a conexão antes de continuar.'); return; }
      if (nativeCheckoutPending) { rejectRepeated(event); return; }
      nativeCheckoutPending = true;
      nativeStatus('Abrindo o checkout. Aguarde a confirmação; se a página não avançar, confira o carrinho.');
      return;
    }
    const button = event.target?.closest?.('button[name="add"],input[name="add"],#add-to-cart,#AddToCart,.ProductForm__AddToCart');
    if (!button || button.disabled) return;
    const form = nativeForm(button);
    if (!form) { cancelNative(event, 'Confira as opções na página do produto antes de continuar.'); return; }
    const state = nativeState(form);
    if (state.error) { cancelNative(event, state.error); return; }
    if (nativeSubmissions.has(form)) { rejectRepeated(event); return; }
    if (typeof form.closest('product-form-component')?.handleSubmit === 'function') return;
    const lock = { allowImmediateSubmit: true };
    nativeSubmissions.set(form, lock);
    nativeStatus('Adicionando ao carrinho. Aguarde a confirmação; se a página não avançar, confira o carrinho.');
  }, true);
  document.addEventListener('submit', (event) => {
    const cartForm = event.target;
    const isCartForm = cartForm?.id === 'cart-form' || cartForm?.classList?.contains('ymp-CrtpageMainFrm');
    if (isCartForm && event.submitter?.name !== 'update') {
      if (!/\/cart\/?$/.test(window.location.pathname)) { navigateToCart(event); return; }
      cancelNative(event, 'Use o botão de checkout quando a conexão estiver pronta.'); return;
    }
    const form = nativeForm(event.target);
    if (!form) return;
    if (typeof form.closest('product-form-component')?.handleSubmit === 'function') return;
    const state = nativeState(form);
    if (state.error) { cancelNative(event, state.error); return; }
    const lock = nativeSubmissions.get(form);
    if (lock?.allowImmediateSubmit) { lock.allowImmediateSubmit = false; return; }
    if (lock) { rejectRepeated(event); return; }
    nativeSubmissions.set(form, { allowImmediateSubmit: false });
    nativeStatus('Adicionando ao carrinho. Aguarde a confirmação; se a página não avançar, confira o carrinho.');
  }, true);
  window.addEventListener('pageshow', () => { nativeSubmissions = new WeakMap(); nativeCheckoutPending = false; });
  const previewCards = new WeakSet();
  function initializeCollectionPreviews(scope = document) {
    scope.querySelectorAll('[data-orso-native-card]').forEach((card) => {
      if (previewCards.has(card)) return;
      previewCards.add(card);
      const image = card.querySelector('[data-orso-native-secondary]');
      const button = card.querySelector('[data-orso-native-preview]');
      if (!image || !button) return;
      let mouse = false;
      let pinned = false;
      let failed = false;
      const prepare = () => {
        if (image.dataset.srcset) { image.setAttribute('srcset', image.dataset.srcset); delete image.dataset.srcset; }
        if (image.dataset.src) { image.setAttribute('src', image.dataset.src); delete image.dataset.src; }
      };
      const update = () => {
        const focused = card.contains(document.activeElement) && document.activeElement !== button;
        const active = !failed && (pinned || mouse || focused);
        if (active) prepare();
        card.toggleAttribute('data-orso-preview-active', active);
        button.setAttribute('aria-pressed', String(pinned));
      };
      card.addEventListener('pointerenter', (event) => { if (event.pointerType !== 'touch') { mouse = true; update(); } });
      card.addEventListener('pointerleave', () => { mouse = false; update(); });
      card.addEventListener('focusin', update);
      card.addEventListener('focusout', () => window.setTimeout(update, 0));
      button.addEventListener('click', (event) => { event.preventDefault(); pinned = !pinned; update(); });
      image.addEventListener('error', () => { failed = true; pinned = false; mouse = false; card.removeAttribute('data-orso-preview-active'); button.hidden = true; });
    });
  }
  function initialize(scope = document) { initializePeers(scope); initializeCollectionPreviews(scope); }
  window.OrsoNativeBridge = Object.freeze({ initializePeers, initializeCollectionPreviews, canSubmitNative });
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  const main = document.querySelector('#MainContent');
  if (main && typeof MutationObserver === 'function') {
    let queued = false;
    new MutationObserver((records) => {
      if (queued || !records.some((record) => record.addedNodes.length)) return;
      queued = true;
      queueMicrotask(() => { queued = false; initialize(main); });
    }).observe(main, { childList: true, subtree: true });
  }
})();
